import { IComment, ICommentCreate, ICommentCreated } from "@defs/comment";
import { IReferral } from "@defs/referral";
import {
  ITask,
  ITaskCompleted,
  ITaskDeleteResponse,
  ITaskHandle,
  ITaskReopenResponse,
  ITaskUpdateExpiration,
} from "@defs/task";
import {
  ITicket,
  ITicketAssigneesUpdated,
  ITicketObserverRemoveRequest,
  ITicketObserversUpdated,
  ITicketSLAUpdateRequest,
  ITicketSLAUpdateResponse,
  ITicketSuspendResponse,
  ITicketTagDeleteRequest,
  ITicketTagsUpdateRequest,
  ITicketUpdateDescriptionRequest,
  ITicketUpdatePriorityRequest,
  ITicketUpdateTitleRequest,
} from "@defs/ticket";
import {
  ITicketRelated,
  ITicketRelatedRemoveChildRequest,
  ITicketRelatedResponse,
} from "@defs/ticket-related";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ApiResponsePaginated } from "@defs/api-response";
import { ICategory } from "@defs/category";
import { ICustomer } from "@defs/customer";
import { IService } from "@defs/service";
import { ITicketHistory } from "@defs/ticket-history";
import { ITicketReferralUpdateRequest } from "@defs/ticket-referral";
import { IUser } from "@defs/user";
import { PayloadActionFulfilledMeta } from "@defs/types";
import commentsStateHelper from "@helpers/comments-state-helper";
import dayjs from "dayjs";

interface TicketState {
  loading: boolean;
  data: ITicket;
  related: {
    parent: ITicketRelated;
    children: ITicketRelated[];
  };
  users: {
    loading: boolean;
    data: IUser[];
  };
  children_timings: {
    data: ITicketRelated[];
    loading: boolean;
  };
  error: boolean;
  deleting: boolean;
  restoring: boolean;
}

const initialState: TicketState = {
  loading: true,
  data: null,
  related: {
    parent: null,
    children: [],
  },
  users: {
    loading: true,
    data: [],
  },
  children_timings: {
    data: [],
    loading: false,
  },
  error: false,
  deleting: false,
  restoring: false,
};

const ticketSlice = createSlice({
  name: "ticket",
  initialState,
  reducers: {},
  extraReducers: {
    "ticket/detail/pending": (state) => {
      state.loading = true;
      state.data = null;
    },

    "ticket/detail/fulfilled": (state, action: PayloadAction<ITicket>) => {
      state.loading = false;
      state.data = action.payload;
    },

    "ticket/add-to-favorites/fulfilled": (state) => {
      state.data.is_user_favorite = true;
    },

    "ticket/remove-from-favorites/fulfilled": (state) => {
      state.data.is_user_favorite = false;
    },

    "comment/delete/fulfilled": (state, action: PayloadAction<IComment>) => {
      if (state.data) {
        const comment = action.payload;

        state.data.time_spent -= comment.time_spent;

        state.data.comments = commentsStateHelper.updateCommentsAfterDelete(
          state.data.comments,
          comment
        );
      }
    },

    "documents/delete/fulfilled": (state, action: PayloadAction<string>) => {
      if (state.data) {
        const comments = commentsStateHelper.removeDocumentFromComment(
          state.data.comments,
          action.payload
        );

        state.data.comments = comments;
      }
    },

    "comment/create/fulfilled": (
      state,
      action: PayloadAction<
        ICommentCreated,
        string,
        PayloadActionFulfilledMeta<ICommentCreate>
      >
    ) => {
      const { comment, observers } = action.payload;
      const { ticket } = action.meta.arg;

      if (state.data && state.data.id === ticket) {
        state.data.time_spent += comment.time_spent;
        state.data.comments.push(comment);
        state.data.users.push(...observers);
      }
    },

    "ticket/history/fulfilled": (
      state,
      action: PayloadAction<ApiResponsePaginated<ITicketHistory[]>>
    ) => {
      const { meta, data, links } = action.payload;

      const records =
        meta.current_page > 1 ? [...state.data.history.data, ...data] : data;

      state.data.history = { data: records, meta, links };
    },

    "ticket/assignees/update/fulfilled": (
      state,
      action: PayloadAction<ITicketAssigneesUpdated>
    ) => {
      state.data.task.group = action.payload.group;
      state.data.task.handler = action.payload.handler;
    },

    "ticket/observers/remove/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITicketObserverRemoveRequest>
      >
    ) => {
      const { type, id } = action.meta.arg;

      if (type === "user") {
        state.data.users = state.data.users.filter((u) => u.id !== id);
      } else {
        state.data.groups = state.data.groups.filter((g) => g.id !== id);
      }
    },

    "ticket/observers/add/fulfilled": (
      state,
      action: PayloadAction<ITicketObserversUpdated>
    ) => {
      const { groups, users } = action.payload;

      state.data.groups = groups;
      state.data.users = users;
    },

    "ticket/update/title/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITicketUpdateTitleRequest>
      >
    ) => {
      state.data.title = action.meta.arg.title;
    },

    "ticket/update/description/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITicketUpdateDescriptionRequest>
      >
    ) => {
      state.data.description = action.meta.arg.description;
    },

    "ticket/update/category/fulfilled": (
      state,
      action: PayloadAction<ICategory>
    ) => {
      state.data.category = action.payload;
      state.data.sla = null;
      state.data.tags = [];
    },

    "ticket/update/priority/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITicketUpdatePriorityRequest>
      >
    ) => {
      state.data.priority = action.meta.arg.priority;
    },

    "ticket/update/service/fulfilled": (
      state,
      action: PayloadAction<IService>
    ) => {
      state.data.service = action.payload;
    },

    "ticket/suspend/fulfilled": (
      state,
      action: PayloadAction<ITicketSuspendResponse>
    ) => {
      const { suspend, suspendedFor } = action.payload;
      state.data.status = suspend ? "Sospeso" : "Preso in carico";
      state.data.suspended_for = suspendedFor;
    },

    "task/reopen/fulfilled": (
      state,
      action: PayloadAction<
        ITaskReopenResponse,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      if (state.data) {
        const id = action.meta.arg;

        state.data.status = action.payload.ticket_status;

        if (state.data.task.id === id) {
          state.data.resolved_in = null;
          state.data.task.completed_at = null;
        } else {
          state.data.tasks = state.data.tasks.map((task) => {
            if (task.id === id) {
              return { ...task, completed_at: null };
            }

            return task;
          });
        }
      }
    },

    "tasks/complete/fulfilled": (
      state,
      action: PayloadAction<ITaskCompleted>
    ) => {
      const { task, tasks } = state.data;
      const { task: data, ticket } = action.payload;

      if (task.id === data.id) {
        state.data.task = data;
      } else {
        state.data.tasks = tasks.map((t) => (t.id === data.id ? data : t));
      }

      state.data.resolved_in = ticket.resolved_in;
      state.data.status = ticket.status;
    },

    "tasks/create/fulfilled": (state, action: PayloadAction<ITask>) => {
      const tasksList = [...state.data.tasks, action.payload].sort((a, b) => {
        const first = new Date(a.expires_at);
        const second = new Date(b.expires_at);

        if (dayjs(first).isSame(second)) {
          return 0;
        }

        return dayjs(first).isBefore(second) ? -1 : 1;
      });

      state.data.tasks = tasksList;
    },

    "tasks/handle/fulfilled": (
      state,
      action: PayloadAction<
        ITaskHandle,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      if (state.data) {
        const id = action.meta.arg;
        const { task, tasks } = state.data;
        const { handler, handled_at, handled_in } = action.payload;

        if (task.id === id) {
          state.data.status = "Preso in carico";
          state.data.handled_in = handled_in;
          state.data.task = { ...task, handler, handled_at };
        } else {
          state.data.tasks = tasks.map((t) => {
            if (t.id === id) {
              return { ...t, handler, handled_at };
            }

            return t;
          });
        }
      }
    },

    "tasks/delete/fulfilled": (
      state,
      action: PayloadAction<
        ITaskDeleteResponse,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      if (state.data) {
        const id = action.meta.arg;
        const status = action.payload.ticket_status;

        state.data.status = status;
        state.data.tasks = state.data.tasks.filter((t) => t.id !== id);
      }
    },

    "tasks/update/fulfilled": (state, action: PayloadAction<ITask>) => {
      if (state.data && state.data.tasks) {
        const task = action.payload;

        state.data.tasks = state.data.tasks.map((t) => {
          if (t.id === task.id) {
            const { handler, group, title, description, expires_at } =
              action.payload;

            return { ...t, handler, group, title, description, expires_at };
          }
          return t;
        });
      }
    },

    "ticket/delete/fulfilled": (state) => {
      state.data.deleted = true;
    },

    "ticket/restore/fulfilled": (state) => {
      state.data.deleted = false;
    },

    "ticket/related/get/fulfilled": (
      state,
      action: PayloadAction<ITicketRelatedResponse>
    ) => {
      state.related.parent = action.payload.parent;
      state.related.children = action.payload.children;
    },

    "ticket/related/delete/parent/fulfilled": (state) => {
      state.related.parent = null;
    },

    "ticket/related/delete/child/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITicketRelatedRemoveChildRequest>
      >
    ) => {
      const {
        related,
        children_timings: { data: timings },
      } = state;

      const id = action.meta.arg.child;

      state.data.children_count -= 1;

      state.related.children = related.children.filter(
        (item) => item.id !== id
      );

      if (timings.length) {
        let timeToBeRemoved = 0;

        state.children_timings.data = timings.filter((c) => {
          if (c.id === id) {
            timeToBeRemoved = c.time_spent;
          }

          return c.id !== id;
        });

        state.data.children_time_spent -= timeToBeRemoved;
      }
    },

    "ticket/related/store/parent/fulfilled": (
      state,
      action: PayloadAction<ITicketRelated>
    ) => {
      state.related.parent = action.payload;
    },

    "ticket/related/store/children/fulfilled": (
      state,
      action: PayloadAction<ITicketRelated[]>
    ) => {
      const current = state.related.children;
      const timings = state.children_timings.data;
      const newest = action.payload;

      state.data.children_count += newest.length;

      state.related.children = [...current, ...newest].sort((a, b) =>
        a.title.localeCompare(b.title)
      );

      // Aggiungo i dati anche nella lista dei tempi
      const withRegisteredTime = newest.filter((t) => t.time_spent > 0);

      state.children_timings.data = [...timings, ...withRegisteredTime].sort(
        (a, b) => a.title.localeCompare(b.title)
      );

      // Aggiorno il totale del tempo speso
      let timeSpent = 0;
      withRegisteredTime.forEach((c) => (timeSpent += c.time_spent));
      state.data.children_time_spent += timeSpent;
    },

    "ticket/customer/set/fulfilled": (
      state,
      action: PayloadAction<ICustomer>
    ) => {
      state.data.customer = action.payload;
    },

    "ticket/estimated-time/update/fulfilled": (
      state,
      action: PayloadAction<number>
    ) => {
      state.data.estimated_time = action.payload;
    },

    "ticket/timings/children/get/pending": (state) => {
      state.children_timings.data = [];
      state.children_timings.loading = true;
    },

    "ticket/timings/children/get/fulfilled": (
      state,
      action: PayloadAction<ITicketRelated[]>
    ) => {
      state.children_timings.data = action.payload;
      state.children_timings.loading = false;

      let timeSpent = 0;
      action.payload.forEach((c) => (timeSpent += c.time_spent));
      state.data.children_time_spent = timeSpent;
    },

    "ticket/public-emails-cc/update/fulfilled": (
      state,
      action: PayloadAction<string>
    ) => {
      state.data.cc_customer_emails = action.payload;
    },

    "ticket/referrals/get/fulfilled": (
      state,
      action: PayloadAction<IReferral[]>
    ) => {
      state.data.customer.referrals = action.payload;
    },

    "ticket/referral/set/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITicketReferralUpdateRequest>
      >
    ) => {
      const { refId } = action.meta.arg;

      const referral = state.data.customer.referrals.find(
        (ref) => ref.id === refId
      );

      state.data.referral = referral;
      state.data.visibility = "Pubblico";
    },

    "ticket/users/list/get/pending": (state) => {
      state.users.loading = true;
      state.users.data = [];
    },

    "ticket/users/list/get/fulfilled": (
      state,
      action: PayloadAction<IUser[]>
    ) => {
      state.users.loading = false;
      state.users.data = action.payload;
    },

    "task/expiration/update/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITaskUpdateExpiration>
      >
    ) => {
      if (state.data) {
        const { id } = action.meta.arg;
        const expires_at = action.payload;

        if (state.data.task.id === id) {
          state.data.task.expires_at = expires_at;
        } else {
          state.data.tasks = state.data.tasks.map((t) => {
            if (t.id === id) {
              return { ...t, expires_at };
            }

            return t;
          });
        }
      }
    },

    "ticket/sla/update/fulfilled": (
      state,
      action: PayloadAction<
        ITicketSLAUpdateResponse,
        string,
        PayloadActionFulfilledMeta<ITicketSLAUpdateRequest>
      >
    ) => {
      const { sla: slaID } = action.meta.arg;
      const { handled_in, resolved_in, suspended_for, opened_since } =
        action.payload;

      const sla = state.data.category.slas.find((sla) => sla.id === slaID);

      if (sla) {
        state.data.sla = sla;
      }

      state.data.handled_in = handled_in;
      state.data.resolved_in = resolved_in;
      state.data.suspended_for = suspended_for;
      state.data.opened_since = opened_since;
    },

    "ticket/tags/update/fulfilled": (
      state,
      action: PayloadAction<
        boolean,
        string,
        PayloadActionFulfilledMeta<ITicketTagsUpdateRequest>
      >
    ) => {
      state.data.tags = state.data.category.tags.filter((tag) =>
        action.meta.arg.tags.includes(tag.id)
      );
    },

    "ticket/tag/delete/fulfilled": (
      state,
      action: PayloadAction<
        boolean,
        string,
        PayloadActionFulfilledMeta<ITicketTagDeleteRequest>
      >
    ) => {
      state.data.tags = state.data.tags.filter(
        (t) => t.id !== action.meta.arg.tag
      );
    },

    "task/detail/fulfilled": (state, action: PayloadAction<ITask>) => {
      const task = action.payload;
      const index = state.data.tasks.findIndex((t) => t.id === task.id);

      if (index !== -1) {
        state.data.tasks[index].unread_comments = 0;
      }
    },
  },
});

export const ticketActions = ticketSlice.actions;

export default ticketSlice.reducer;
